import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Callout, Chip, FormField, Modal, OptionCard, Select } from '@/components/ui';
import { AddRoleCombobox } from './AddRoleCombobox';
import { RoleMapStaleCatalogCard } from './RoleMapStaleCatalogCard';
import { isStaleCatalogError } from './useRoleMapWrites';
import type { RoleMapTableVM } from './useRoleMapTable';
import styles from './RoleMapAddModal.module.css';

/**
 * EAA-1837: "+ Thêm liên kết" — the header's primary action.
 *
 * Deliberately allows SEVERAL app roles in one submit: that is how a multi-role
 * mapping (`teacher_lead → lms: teacher + admin_class`) comes into existence,
 * since it is N `RoleMap` rows sharing one `(role_key, application)` pair. The
 * app-role picker is the EAA-1817 `AddRoleCombobox` unchanged — it lists only
 * what `GET /admin/app-roles?application=` reports and accepts no free text, so
 * a link can never be created against a role the app does not declare.
 *
 * `role_key` options (`vm.standardRoles`) come from the server's governed
 * role-key vocabulary (`GET /role-keys`, EAA-1817) rather than the acting
 * org's own Levels — see `standardRolesFromRoleKeys` in `types.ts` for the
 * fallback when that vocabulary is empty; when even the fallback has nothing,
 * the modal says so instead of offering a free-text field that would create a
 * mapping nothing resolves to.
 */
export function RoleMapAddModal({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const [roleKey, setRoleKey] = useState('');
    const [appRoles, setAppRoles] = useState<string[]>([]);

    const appCode = vm.addApp;

    useEffect(() => {
        if (!vm.addOpen) return;
        setRoleKey('');
        setAppRoles([]);
        vm.setAddApp(null);
        vm.createMutation.reset();
        // Only reset when the modal opens; `vm.setAddApp`/`vm.createMutation.reset`
        // are stable across renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vm.addOpen]);

    // Changing the application invalidates any chip picked from the previous
    // app's catalog — those keys mean nothing to the new app.
    useEffect(() => {
        setAppRoles([]);
    }, [appCode]);

    /**
     * Accounts currently holding the chosen standard role, in the scope being
     * written. Taken from the grouped rows already on screen rather than fetched:
     * `RoleMapGroup.accountCount` is exactly this number (see `useAccountCounts`),
     * and a role with no links yet has no group — hence `null`, which the copy
     * handles by stating the effect without a figure instead of claiming zero.
     */
    const selectedRoleAccountCount =
        vm.visibleGroups.find((group) => group.role_key === roleKey)?.accountCount ?? null;

    /* EAA-1817: `scopeLabel` is gone. It backed a read-only sentence saying which
       scope the TOOLBAR happened to be on — the modal's stand-in for the choice
       frame 55b actually draws. That choice is now a real control ("Phạm vi áp
       dụng" below), so a sentence restating the toolbar would contradict it. */
    const enabledApps = useMemo(() => vm.apps.filter((app) => app.enabled), [vm.apps]);
    const selectedApp = useMemo(
        () => vm.apps.find((app) => app.code === appCode) ?? null,
        [vm.apps, appCode]
    );

    // EAA-1817: the "Tạo ngay trong {app}" escape hatch (org-scoped role
    // creation from inside this modal) is REMOVED, not hidden. This phase
    // manages system / ecosystem-default app roles only, and
    // `POST /admin/app-roles` always upserts the row ORG-SCOPED — so offering
    // it here contradicted the console's current scope. It was already gated
    // to `vm.scope === 'org'` for that exact reason; the fix is to drop the
    // affordance rather than keep guarding it. `/console/apps` still offers
    // role creation unconditionally, outside any linking flow. Bringing this
    // back requires the console to manage org-scoped app roles as a real
    // feature, not just re-adding the button.
    const canSubmit = Boolean(roleKey) && Boolean(appCode) && appRoles.length > 0 && !vm.addBusy;

    const { error: createError, reset: resetCreateError } = vm.createMutation;
    const staleError = isStaleCatalogError(createError) ? createError : null;

    // EAA-1817: "Thẻ đã được bỏ khỏi danh sách" (frame 57) — the chip that
    // tripped the catalog check is removed from the pending selection the
    // moment the error lands, so "Thử lại" can never resubmit the exact same
    // broken request.
    useEffect(() => {
        if (!staleError) return;
        setAppRoles((prev) => prev.filter((key) => key !== staleError.appRole));
    }, [staleError]);

    const staleAppName =
        vm.apps.find((app) => app.code === staleError?.application)?.name ??
        staleError?.application ??
        '';

    const submit = () => {
        if (!appCode || appRoles.length === 0) return;
        void vm.createLinks({ role_key: roleKey, application: appCode, appRoles });
    };

    return (
        <>
            <Modal
                // EAA-1804: every kit component's root should be reachable by
                // className per the convention sweep — this Modal had none.
                className="role-map-add-modal"
                // Frame 55b: 660px. antd's default is 520, which forced every
                // field into one column no matter what the grid said.
                width={660}
                open={vm.addOpen}
                onCancel={vm.closeAdd}
                title={t('console.roleMap.addModal.title')}
                footer={
                    <>
                        <Button onClick={vm.closeAdd} disabled={vm.addBusy}>
                            {t('common.cancel')}
                        </Button>
                        <Button type="primary" disabled={!canSubmit} onClick={submit}>
                            {vm.addBusy
                                ? t('console.roleMap.addModal.saving')
                                : // EAA-1845: `roleCount` (not `count`) on purpose — i18next
                                  // reserves the `count` option key for its own pluralization
                                  // rules and requires it to be a number, but this needs the
                                  // original quirky "" fallback (see the historical `.trim()`
                                  // note below) to keep matching the pre-i18n markup exactly.
                                  t('console.roleMap.addModal.submitButton', {
                                      roleCount: appRoles.length || '',
                                  }).trim()}
                        </Button>
                    </>
                }
            >
                {/* Frame 55b lays the body out as `1fr 1fr`: "Vai trò chuẩn" and
                    "Ứng dụng" sit side by side, and the wide fields below span
                    both columns. This used to hard-code `1fr`, stacking all of
                    them and making the modal twice as tall as the frame. */}
                <div className={`portal-admin-form ${styles.addModalGrid}`}>
                    <FormField
                        label={t('console.roleMap.addModal.roleKeyLabel')}
                        hint={
                            // EAA-1817: copy updated for the new source — `standardRoles` now
                            // comes from the governed role-key vocabulary (`GET /role-keys`),
                            // falling back to this org's existing rows, not from Levels — so
                            // "chưa khai báo vai trò chuẩn" no longer points at "Cơ cấu tổ
                            // chức" specifically; nothing governed exists anywhere yet.
                            vm.standardRoles.length === 0
                                ? t('console.roleMap.addModal.noStandardRolesHint')
                                : undefined
                        }
                    >
                        {/* EAA-1817: kit `Select`'s `onChange` passes the selected value
                        directly (not a DOM event), so `event.target.value` becomes just
                        `value`. The empty-string placeholder is kept as a selectable
                        option (not the `placeholder` prop) to match the previous
                        native-select behavior exactly. */}
                        <Select
                            value={roleKey}
                            onChange={(value) => setRoleKey(value as string)}
                            style={{ width: '100%' }}
                            options={[
                                {
                                    value: '',
                                    label: t('console.roleMap.addModal.roleKeyPlaceholderOption'),
                                },
                                ...vm.standardRoles.map((role) => ({
                                    value: role.role_key,
                                    label: `${role.role_key} — ${role.name}`,
                                })),
                            ]}
                        />
                    </FormField>

                    <FormField label={t('console.roleMap.addModal.appLabel')}>
                        <Select
                            value={appCode ?? ''}
                            onChange={(value) => vm.setAddApp((value as string) || null)}
                            style={{ width: '100%' }}
                            options={[
                                {
                                    value: '',
                                    label: t('console.roleMap.addModal.appPlaceholderOption'),
                                },
                                ...enabledApps.map((app) => ({
                                    value: app.code,
                                    label: `${app.code} — ${app.name}`,
                                })),
                            ]}
                        />
                    </FormField>

                    {/* Frame 55b's "Phạm vi áp dụng" — two radio cards. It was
                        missing entirely; the modal only printed a sentence saying
                        which scope the toolbar happened to be on, so the operator
                        could not see the choice the frame offers, let alone make
                        it.

                        "Chọn đơn vị" ships DISABLED with its reason. The endpoint
                        supports it (`POST /role-maps` takes `organization_id`),
                        but this phase manages SYSTEM / ecosystem-default links
                        only — the same instruction that removed the per-org
                        create-role affordances. Rendering the option greyed with
                        the reason attached says "this exists and is not yours
                        yet"; omitting it would quietly redraw the frame, and
                        enabling it would contradict the phase. */}
                    <div className={styles.addModalWide}>
                        <span className={styles.addRolesLabel}>
                            {t('console.roleMap.addModal.scopeFieldLabel')}
                        </span>
                        <div className={styles.addModalScope}>
                            <OptionCard
                                inputType="radio"
                                name="role-map-add-scope"
                                value="default"
                                title={t('console.roleMap.addModal.scopeAllTitle')}
                                description={
                                    // With no application picked yet there is no
                                    // name to put in the sentence — `scopeLabel`
                                    // was standing in and read as "Mọi đơn vị
                                    // đang dùng Mặc định", which names a scope
                                    // where an application belongs.
                                    selectedApp
                                        ? t('console.roleMap.addModal.scopeAllDescription', {
                                              appName: selectedApp.name,
                                          })
                                        : t('console.roleMap.addModal.scopeAllDescriptionNoApp')
                                }
                                selected
                                onSelect={() => undefined}
                            />
                            <OptionCard
                                inputType="radio"
                                name="role-map-add-scope"
                                value="unit"
                                title={t('console.roleMap.addModal.scopeUnitTitle')}
                                description={t('console.roleMap.addModal.scopeUnitDescription')}
                                selected={false}
                                disabled
                                disabledReason={t('console.roleMap.addModal.scopeUnitDisabled')}
                                onSelect={() => undefined}
                            />
                        </div>
                    </div>

                    <div className={`${styles.addRoles} ${styles.addModalWide}`}>
                        <span className={styles.addRolesLabel}>
                            {t('console.roleMap.addModal.appRolesLabel')}
                        </span>
                        <div className={styles.addRolesChips}>
                            {appRoles.map((key) => (
                                <Chip
                                    key={key}
                                    tone="mapped"
                                    onRemove={() =>
                                        setAppRoles((prev) => prev.filter((k) => k !== key))
                                    }
                                    removeLabel={t('console.roleMap.addModal.removeChipLabel', {
                                        key,
                                    })}
                                >
                                    {key}
                                </Chip>
                            ))}
                            <AddRoleCombobox
                                appName={
                                    appCode ?? t('console.roleMap.addModal.genericAppFallback')
                                }
                                options={vm.addAppRoles}
                                loading={vm.addAppRolesLoading}
                                linkedAppRoles={appRoles}
                                onSelect={(key) => setAppRoles((prev) => [...prev, key])}
                                disabled={!appCode}
                            />
                        </div>
                    </div>

                    {/* EAA-1817 (design frame 55b): the CONSEQUENCE line. The modal
                        used to end at the picker, so the operator committed a
                        write whose effect — N accounts gaining access to an
                        application at the next provisioning run — was never
                        stated. The account count is the one for this standard
                        role in the current scope, already on the row the picker
                        selected, so this costs no request.

                        Two facts the frame insists on and the code must not drop:
                        the grant is NOT immediate (it lands on the next run), and
                        the write is logged. */}
                    {appRoles.length > 0 && roleKey && (
                        <Callout tone="info">
                            <Trans
                                i18nKey={
                                    selectedRoleAccountCount === null
                                        ? 'console.roleMap.addModal.consequenceNoCount'
                                        : 'console.roleMap.addModal.consequence'
                                }
                                values={{
                                    count: selectedRoleAccountCount ?? 0,
                                    roleCount: appRoles.length,
                                    appName: selectedApp?.name ?? appCode,
                                }}
                                components={{ strong: <strong /> }}
                            />{' '}
                            {t('console.roleMap.addModal.consequenceUndo')}
                        </Callout>
                    )}

                    {staleError && (
                        <RoleMapStaleCatalogCard
                            appName={staleAppName}
                            appRole={staleError.appRole}
                            syncBusy={vm.syncMutation.isPending}
                            onSync={() => {
                                vm.syncMutation.mutate();
                                resetCreateError();
                            }}
                            onRetry={appRoles.length > 0 ? submit : undefined}
                        />
                    )}
                </div>
            </Modal>
        </>
    );
}
